import { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getImage, getSrc } from 'gatsby-plugin-image';
import { Heading } from '@uk-source-web/heading';
import { Span } from '@uk-source-web/span';
import { Icon } from '@uk-source-web/icon';
import Image from '@legacy-vfuk/core-image';
import { getOriginalPrice } from '../../../../../utils/getOriginalPrices';

import {
  UserBasketItem,
  UserBasketItemBase,
  clearDiscount,
  isInBasket,
  removeFromBasket,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { checkForZeroOrNull } from '@/helpers/nullOrZero';
import { OPCOOptions } from '@/types/Services.types';

import BasketPrice from '../BasketPrice';
import BasketItemDelete from '../BasketItemDelete';
import BasketItemBundle from '../BasketItemBundle';
import { BasketItemProps } from '../SWBasket.types';
import BasketQuantitySelector from '../BasketQuantitySelector';
import { NULL_PRICE_VALUE, getBundleIncludes, getMonthlyPrice, getUpfrontPrice } from '../SWBasket.helper';

import BasketItemStyles, {
  BasketAccordion,
  BasketAccordionButton,
  BasketAccordionButtonWrapper,
  BasketAccordionItem,
  BasketAccordionPanel,
  BasketAccordionPanelWrapper,
  BasketItemContents,
  BasketItemFlag,
  BasketItemGridPrices,
  BasketItemInner,
  BasketItemThumb,
  BasketItemThumbWrapper,
  BasketItemTitle,
  AdditionalData,
  DiscountWrapper,
  BasketItemNormal,
} from './BasketItem.styles';
import handleTranslation from '@/helpers/handleTranslation';
import StateNotification from '@legacy-vfuk/core-state-notification';
import { CurrencyPrice, HeadingPrice, SuffixPrice } from '../BasketPrice/BasketPrice.styles';
import EditMicrosoftDataForm from './MicrosoftDataForm/EditMicrosoftDataForm';
import { useValid } from '@/contexts/TenantContext/TenantContext';
import { translatedMDFValues } from '../translatedValues';
import { useBasketSummary } from '@/contexts/BasketSummaryContext/BasketSummary';

const OPCO = process.env.GATSBY_OPCO;

const BasketItem = ({
  discountData,
  continueBtn,
  microsoftItemState,
  microsoftState,
  needsMCA,
  isSummaryLoaded = true,
  ...item
}: BasketItemProps & {
  discountData: { discountData: string | undefined };
  continueBtn: React.Dispatch<React.SetStateAction<boolean>>;
  microsoftItemState: React.Dispatch<SetStateAction<string>>;
  microsoftState: string;
  needsMCA?: boolean;
  needsAdditionalInfo?: boolean;
  isSummaryLoaded?: boolean;
}) => {
  const { increaseDecreaseAct = true, quantity } = item;

  //extract discount data and trype
  const discountType = discountData.discountData?.split(':')[0];
  const discountAmount = parseInt((discountData.discountData || '')?.split(':')[1]);
  
  const { basketSummary } = useBasketSummary();
  const needsAdditionalInfo = basketSummary?.data.item?.find((basketItem) => basketItem.productId === item.productId)?.needsAdditionalInformation;
  const { validForm } = useValid();

  // update additional state

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userBasket = useAppSelector(state => state.userBasket.data);

  //check if it is a microsoft product or not
  const isMicrosoftProduct = item.msftProduct;
  // Hide card while summary is loading; after load, keep backward-compatible defaults.
  const effectiveNeedsMCA = needsMCA ?? false;
  const effectiveNeedsAdditionalInfo = needsAdditionalInfo ?? true;
  const shouldShowMicrosoftInfoCard =
    !!isMicrosoftProduct && isSummaryLoaded && (effectiveNeedsMCA || effectiveNeedsAdditionalInfo);

  const [showForm, setShowForm] = useState(false);
  const showFormRef = useRef(false);
  const intentionalClose = useRef(false);
  
  //acount ID - needed for localStorage persistence
  const userSessionData = sessionStorage.getItem('persist:vf_userSession');
  let accountId = '';
  if (userSessionData) {
    const parsedData = JSON.parse(userSessionData);
    accountId = JSON.parse(parsedData.user).userId;
  }

  // Load mcaVerified from localStorage on mount
  const [mcaVerified, setMcaVerified] = useState(() => {
    if (!accountId) return false;
    const savedMcaVerified = localStorage.getItem(`${accountId}_mcaVerified`);
    return savedMcaVerified === 'true';
  });

  // Only show MCA warning if needsMCA is explicitly true from API (not undefined/loading)
  // While basket summary is loading (needsMCA === undefined), show standard error behavior
  const shouldShowMcaWarning = needsMCA === true && !mcaVerified;

  // Persist mcaVerified to localStorage whenever it changes
  useEffect(() => {
    if (!accountId) return;
    
    if (mcaVerified) {
      localStorage.setItem(`${accountId}_mcaVerified`, 'true');
      return;
    }
    
    // Only remove if API explicitly says MCA is not needed (not undefined/loading)
    if (needsMCA === false) {
      localStorage.removeItem(`${accountId}_mcaVerified`);
    }
  }, [mcaVerified, accountId, needsMCA]);

  // Sync mcaVerified state with API value - API always takes precedence
  useEffect(() => {
    // Only sync when needsMCA has a value from API (not undefined/loading)
    if (needsMCA === undefined) {
      // While loading, restore from localStorage if available
      if (accountId && !mcaVerified) {
        const savedMcaVerified = localStorage.getItem(`${accountId}_mcaVerified`);
        if (savedMcaVerified === 'true') {
          setMcaVerified(true);
        }
      }
      return; // Don't do anything else while loading
    }
    
    // If API says MCA is not needed, automatically mark as verified and clear localStorage
    if (needsMCA === false) {
      setMcaVerified(true);
      if (accountId) {
        localStorage.removeItem(`${accountId}_mcaVerified`);
      }
    } else if (needsMCA === true && accountId && !mcaVerified) {
      // If API says MCA is needed and state is false, check localStorage
      const savedMcaVerified = localStorage.getItem(`${accountId}_mcaVerified`);
      if (savedMcaVerified === 'true') {
        setMcaVerified(true);
      }
    }
  }, [needsMCA, accountId, mcaVerified]);

  // Wrapper for onMcaVerified callback to ensure immediate persistence
  const handleMcaVerified = useCallback((verified: boolean) => {
    if (accountId && verified) {
      // Persist immediately to localStorage before updating state
      localStorage.setItem(`${accountId}_mcaVerified`, 'true');
    }
    setMcaVerified(verified);
  }, [accountId]);

  //open additional info section by setting it by true
  const handleEditClick = () => {
    showFormRef.current = true; // Allow reopening
    setShowForm(true);
  };

  const handleClose = () => {
    showFormRef.current = false;
    intentionalClose.current = true;
    setShowForm(false); // Close the form
  };

  const handleSubmit = (data: Record<string, string>) => {
    if (Object.keys(formErrors).length === 0) {
      continueBtn(false);
      microsoftItemState('success'); // Update state to 'success'
    }

    // Close the modal after saving
    handleClose();
  };

  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});

  //set error object
  const handleErrors = (newErrors: Record<string, boolean>) => {
    setFormErrors(newErrors);
  };

  //fresh copy of the userBasket slice
  const currentItem: UserBasketItem | undefined = userBasket.items.filter(
    currentItem => currentItem.productId === item?.productId
  )[0];

  
  
  const { discountData: userDiscountData } = useAppSelector(state => state.userDiscount);

  const [originalPrice, setOriginalPrice] = useState<number>(0);

// Find the current item in basket summary when it's available
useEffect(() => {
  
  if (!basketSummary?.data?.item || !currentItem) {
    return;
  }
  const summaryItem = basketSummary.data.item.find(
    item => item.productId === currentItem.productId
  );

  if (summaryItem?.productRecurringCharges) {
    
    setOriginalPrice(summaryItem.productRecurringCharges);
  }

   
}, [basketSummary]);

// Initial price set when component mounts (before summary loads)
useEffect(() => {
  if (currentItem) {
    const initialPrice = getOriginalPrice(currentItem.dxlInformation.id);
    setOriginalPrice(initialPrice);
  }
}, [currentItem]);
  
  // Find discount for current product and extract charge
  const discountedItem = userDiscountData?.itemDiscount?.find(
    (discount) => discount.productId === item.productId
  );
  const discountCharge = discountedItem?.productRecurringCharges ?? 0;

  const bundleIncludes = getBundleIncludes(item);
  const hasBundleIncludes = bundleIncludes?.length > 0;
  const hasUpfrontPrice = OPCO === OPCOOptions.IRELAND;

  const thumbnailIconSrc =
    typeof item.thumbnailIcon == 'string'
      ? item.thumbnailIcon
      : getSrc(item.thumbnailIcon) ?? getImage(item.thumbnailIcon)?.placeholder?.fallback;

  const upFrontPrice = useMemo(() => getUpfrontPrice(currentItem, quantity), [currentItem]);
  const monthlyPrice = useMemo(() => getMonthlyPrice(currentItem), [currentItem]);

  const isUpfrontPriceValid = upFrontPrice !== undefined && !isNaN(Number(upFrontPrice));
  const isMonthlyPriceValid = monthlyPrice !== undefined && !isNaN(Number(monthlyPrice));

  const currentFlag = item.configTab?.toLowerCase().includes('bundle') ? 'Bundle' : item.configTab;
  const quantityTrans = handleTranslation('Quantity', 'Quantidade');
  const monthlyTrans = handleTranslation('Monthly', 'Valor mensal');

  // Helper function to render monthly price with discount logic
  const renderMonthlyPrice = () => {
    const monthlyPriceValue = checkForZeroOrNull(originalPrice * (quantity || 1), NULL_PRICE_VALUE);
    const basePriceProps = {
      originalPrice,
      label: monthlyTrans,
    };

    // If upfront discount is applied, show monthly price without discount
    if (hasUpfrontPrice && discountAmount > 0 && isUpfrontPriceValid) {
      return (
        <BasketPrice
          {...basePriceProps}
          value={monthlyPriceValue}
          isNormal={true}
        />
      );
    }

    // If monthly discount is applicable
    if (discountAmount > 0 && isMonthlyPriceValid && discountCharge > 0) {
      return (
        <DiscountWrapper>
          <BasketPrice
            {...basePriceProps}
            value={originalPrice}
            hasDiscount={true}
            isNormal={false}
          />
          <BasketPrice
            {...basePriceProps}
            value={discountCharge}
            hasDiscount={false}
            isNormal={false}
            discount={discountAmount}
            discountType={discountType}
          />
        </DiscountWrapper>
      );
    }

    // Default: show normal monthly price
    return (
      <BasketPrice
        {...basePriceProps}
        value={monthlyPriceValue}
        isNormal={true}
      />
    );
  };

  // Derived state for Microsoft info error display
  const microsoftDataNotSaved = localStorage.getItem(`${accountId}_microsoftAdditionalData`) !== 'success';
  const formInvalidWhenRequired = effectiveNeedsAdditionalInfo && !validForm;
  const hasMicrosoftInfoError = microsoftDataNotSaved || formInvalidWhenRequired;

  // MCA notification state - determines if we show MCA warning or standard notification
  // Only show MCA warning if needsMCA is explicitly true from API (not undefined/loading)
  const showMcaWarning = shouldShowMcaWarning;
  
  // Determine notification state - extract nested ternary for better code coverage
  const getNotificationState = (): 'error' | 'success' | 'warn' | 'info' => {
    if (showMcaWarning) {
      return 'warn';
    }
    if (hasMicrosoftInfoError) {
      return 'error';
    }
    return microsoftState as 'error' | 'success' | 'warn' | 'info';
  };
  const notificationState = getNotificationState();
  
  const notificationHeading = showMcaWarning 
    ? translatedMDFValues.mcaStateHeading 
    : t('microsoftStateHeading');

  const handleRemoveItem = () => {
    if (isInBasket(userBasket, item)) {
      dispatch(removeFromBasket(item));
      dispatch(clearDiscount());
    }
  };

  useEffect(() => {
    if (showForm === true && !intentionalClose.current) {
      setShowForm(true);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem('showFormState', String(showForm));

    if (showForm) {
      window.utag?.view({
        event_name: 'ui_interaction',
        component_name: 'Active_Overlay',
        event_category: 'Additional Info Overlay',
        event_label: 'Additional Info Overlay',
        event_action: 'Additional Info Overlay',
        compoennt_type: 'Overlay',
      });
    }
  }, [showForm]);

  const upfront = translatedMDFValues.upfront
  const Edit = translatedMDFValues.Edit;

  return (
    <BasketItemStyles hasFlag={!!currentFlag}>
      <BasketItemNormal>
        <BasketItemThumbWrapper>
          <BasketItemThumb hasThumbnail={!!thumbnailIconSrc}>
            <Image alt="image" sm={{ src: thumbnailIconSrc ?? '' }} />
          </BasketItemThumb>
        </BasketItemThumbWrapper>
        <BasketAccordion allowMultiple allowToggle defaultIndex={[0]}>
          <BasketAccordionItem>
            {({ isExpanded }) => (
              <BasketItemInner>
                <BasketItemTitle>
                  <Heading level={4} weight={3} text={item?.dxlInformation?.name ?? item?.name} />
                  {hasBundleIncludes && (
                    <BasketAccordionButtonWrapper isExpanded={isExpanded}>
                      <BasketAccordionButton>
                        <Span id="basket-accordion-details">{t('seeDetails')}</Span>
                        <Icon appearance="secondary" group="system" name="chevron-down" id="basket-accordion-button" />
                      </BasketAccordionButton>
                    </BasketAccordionButtonWrapper>
                  )}
                </BasketItemTitle>
                <BasketItemContents>
                  <BasketQuantitySelector
                    item={{ increaseDecreaseAct, quantity, ...currentItem }}
                    label={quantityTrans}
                  />

                  <BasketItemGridPrices hasUpfrontPrice={hasUpfrontPrice}>
                    {hasUpfrontPrice && discountAmount > 0 && isUpfrontPriceValid ? (
                      <DiscountWrapper>
                        <BasketPrice
                          originalPrice={originalPrice}
                          label={upfront}
                          value={originalPrice}
                          hasDiscount={true}
                          isNormal={false}
                        />
                        <BasketPrice
                          originalPrice={originalPrice}
                          label={upfront}
                          value={checkForZeroOrNull(upFrontPrice, NULL_PRICE_VALUE)}
                          hasDiscount={false}
                          isNormal={false}
                          discount={discountAmount}
                          discountType={discountType}
                        />
                      </DiscountWrapper>
                    ) : (
                      <BasketPrice
                        originalPrice={originalPrice}
                        label={upfront}
                        value={checkForZeroOrNull(upFrontPrice, NULL_PRICE_VALUE)}
                        isNormal={true}
                      />
                    )}

                    {renderMonthlyPrice()}
                  </BasketItemGridPrices>
                  <BasketItemDelete handleDelete={handleRemoveItem} />
                </BasketItemContents>
                {hasBundleIncludes && (
                  <BasketAccordionPanelWrapper>
                    <BasketAccordionPanel>
                      <BasketItemBundle bundle={bundleIncludes} />
                    </BasketAccordionPanel>
                  </BasketAccordionPanelWrapper>
                )}
              </BasketItemInner>
            )}
          </BasketAccordionItem>
        </BasketAccordion>
      </BasketItemNormal>

      {isMicrosoftProduct && (
        <>
          {shouldShowMicrosoftInfoCard && (
            <AdditionalData>
          <StateNotification
            key={microsoftState}
            state={notificationState}
            heading={{ text: translatedMDFValues.mcaStateHeading , level: 3 }}
            button={{ text: Edit, onClick: handleEditClick }}
          >
            {showMcaWarning && (
              <div style={{ marginBottom: '10px', color: 'red' }}>
                {translatedMDFValues.mcaStateDescription}
              </div>
            )}
            {!showMcaWarning && hasMicrosoftInfoError && (
              <div style={{ color: 'red', marginBottom: '10px' }}>
                {translatedMDFValues.missingOrIncorrectInformation}
              </div>
            )}
          </StateNotification>
          {showForm && (
            <EditMicrosoftDataForm
              onSubmit={handleSubmit}
              isOpen={showForm}
              setIsOpen={setShowForm}
              setMicrosoftAdditionalData={microsoftItemState}
              onErrors={handleErrors}
              onClose={handleClose}
              continueBtn={continueBtn}
              accountId={accountId}
              intentClose={intentionalClose.current}
              needsMCA={effectiveNeedsMCA}
              needsAdditionalInfo={effectiveNeedsAdditionalInfo}
              onMcaVerified={handleMcaVerified}
            />
          )}
            </AdditionalData>
          )}
        </>
      )}
    </BasketItemStyles>
  );
};

export default BasketItem;
